/**
 * @module usePrivateSharedResourcesQuery
 *
 * This module provides a React hook for querying shared private subgraph resources associated with an account.
 * It manages both the querying state and errors, and allows the parent component to set available unregistered resources.
 */
import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

import {
  PrivateSubgraphShareStatus,
  PrivateSubgraphsQuery,
} from 'src/lib/graphqlTypes/types';

import { UrlInfo } from '../app/account/supergraphListPage/newSupergraphOnboarding/shared';

/**
 * Custom hook to manage the querying of shared private subgraph resources.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Function} params.getPrivateSubgraphs - Function to trigger the private subgraphs query.
 * @param {ApolloError | undefined} params.privateSubgraphsError - Apollo error object, if any error occurs.
 * @param {PrivateSubgraphsQuery | undefined} params.privateSubgraphsData - Data from the private subgraphs query.
 * @param {Function} params.setAvailableUnregisteredResources - Function to set the available unregistered resources.
 *
 * @returns {Object} - An object containing:
 *                     - setShouldQueryPrivateSubgraphs: Function to set if the private subgraphs should be queried.
 */
export const usePrivateSharedResourcesQuery = ({
  getPrivateSubgraphs,
  privateSubgraphsError,
  privateSubgraphsData,
  privateSubgraphsLoading,
  availableUnregisteredResources,
  setAvailableUnregisteredResources,
}: {
  getPrivateSubgraphs: () => void;
  privateSubgraphsError: ApolloError | undefined;
  privateSubgraphsData: PrivateSubgraphsQuery | undefined;
  privateSubgraphsLoading: boolean;
  availableUnregisteredResources: UrlInfo[];
  setAvailableUnregisteredResources: (resources: UrlInfo[]) => void;
}) => {
  // State variables
  const [shouldQueryPrivateSubgraphs, setShouldQueryPrivateSubgraphs] =
    useState(false);

  // Query to get the private resources that already exist on this account
  useEffect(() => {
    if (
      !privateSubgraphsError &&
      !privateSubgraphsLoading &&
      privateSubgraphsData &&
      privateSubgraphsData.account?.privateSubgraphs?.length
    ) {
      setAvailableUnregisteredResources(
        privateSubgraphsData.account.privateSubgraphs
          .filter(
            (resource) =>
              resource.status === PrivateSubgraphShareStatus.CONNECTED,
          )
          .map((r) => {
            return {
              domainUrl: r.domainUrl,
              name: r.name || '',
              region: r.region,
              status: r.status || PrivateSubgraphShareStatus.UNKNOWN,
            };
          }),
      );
    }
  }, [
    privateSubgraphsData,
    privateSubgraphsError,
    privateSubgraphsLoading,
    setAvailableUnregisteredResources,
    shouldQueryPrivateSubgraphs,
  ]);

  // Effect to initialize the private subgraphs query.
  useEffect(() => {
    if (
      shouldQueryPrivateSubgraphs === true &&
      availableUnregisteredResources.length === 0
    ) {
      try {
        getPrivateSubgraphs();
      } catch (err) {
        Sentry.captureException(err);
        // eslint-disable-next-line no-console
        console.error('Error getting private subgraphs', err);
      }
    }
  }, [
    availableUnregisteredResources,
    getPrivateSubgraphs,
    shouldQueryPrivateSubgraphs,
  ]);
  // End of Query work

  // Expose function to allow triggering of the private subgraphs query.
  return {
    setShouldQueryPrivateSubgraphs,
  };
};
